import { useCallback, useEffect, useState } from 'react';
import { isSSR } from '@/utils/is';

const getDefaultStorage = (key: string) => {
  if (!isSSR) {
    return localStorage.getItem(key);
  }
  return undefined;
};

function useStorage(key: string, defaultValue?: string): [string, (string: string) => void, () => void] {
  const [storedValue, setStoredValue] = useState<string>(() => getDefaultStorage(key) ?? defaultValue ?? '');

  const setStorageValue = useCallback(
    (value: string) => {
      if (!isSSR) {
        localStorage.setItem(key, value);
        setStoredValue(value);
      }
    },
    [key],
  );

  const removeStorage = useCallback(() => {
    if (!isSSR) {
      localStorage.removeItem(key);
      setStoredValue(defaultValue ?? '');
    }
  }, [defaultValue, key]);

  useEffect(() => {
    if (isSSR) {
      return;
    }
    const storageValue = localStorage.getItem(key);
    setStoredValue(storageValue ?? defaultValue ?? '');
  }, [defaultValue, key]);

  return [storedValue, setStorageValue, removeStorage];
}

export default useStorage;
